import moment from 'moment';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import { useHistory, useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import camelcaseKeys from 'camelcase-keys';
import { osName } from 'react-device-detect';
import apis from '@src/apis';
import { checkVietNam } from '@src/utils/checkCountry';
import actions from '@src/redux/actions';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import ReactDOMServer from 'react-dom/server';
import sanitizeHtml from 'sanitize-html';
import {
  FEATURE,
  FREE_PACKAGE_CODES,
  LIMITED_PREVIEW_PACKAGE_LEVEL,
  PACKAGE_CODE,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import {
  BITRATE,
  ENGLISH_VOICE_CODES,
  LANGUAGE_CODE,
  REQUEST_STATUS,
  VOICE_PROVIDER,
  REGEX,
  BREAK_TIME_REGEX,
  MAX_TRY_LISTENING_CHARACTERS,
  NON_VBEE_VOICE_PROVIDERS,
  OUTPUT_AUDIO,
} from '@src/constants/voice';
import datasenses from '@src/services/dataSenses';
import { splitParagraphIntoSentence } from '@src/services/sentence';
import { BANNER_TYPE } from '@src/constants/banner';
import { isSameToday } from '@src/utils/time';
import {
  DIALOG_V2_TYPE,
  MOBILE_BREAKPOINT,
  NOTIFICATION_DIALOG_V3_TYPE,
  POPUP_TIME_OUT,
  SMS_IDENTITY_PROVIDER,
  ZERO_BREAK_TIME_REGEX,
} from '@src/constants';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import {
  checkFeaturePermission,
  countTextLength,
  countCreditsSynthesis,
} from '@src/services/tts';
import {
  HIDE_PREVIEW_DESCRIPTION,
  LOCAL_STORAGE_EXPIRES_TIME,
  PLAYING_AUDIO_TYPE,
  SET_TIME_AUTO_SAVE,
  SILENCE_AUDIO_URL,
  TTS_VERSION,
  VALID_CHARACTERS_LENGTH_REGEX,
  VALID_CHARACTERS_REGEX,
  VALID_SPEED,
  VOICE_VERSION,
} from '@src/constants/tts';
import { REQUEST_TYPE, WARNING_REQUEST_TYPE } from '@src/constants/request';
import { ERROR_CODE } from '@src/errors/code';
import route from '@src/constants/route';
import {
  convertParagraphsToText,
  convertTextToParagraphs,
} from '@src/services/paragraph';
import CrownImg from '@src/assets/images/crown.png';
import NotificationDialog from '@src/components/NewNotificationDialog';
import NotificationDialogMobile from '@src/components/NotificationDialogMobile';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import { RECAPTCHA_ACTION, RECAPTCHA_TYPE } from '@src/constants/recaptcha';
import { RECAPTCHA_ENTERPRISE_SITE_KEY, TTS_SYNTHESIS_URL } from '@src/configs';
import { PING_INTERVAL, WS_TYPE } from '@src/constants/websocket';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import ActionDialog from '@src/components/Dialog/ActionDialog';
import UpgradeImg from '@src/assets/images/space-shuttle.png';
import TrialPreviewImage from '@src/assets/icons/icon-preview.png';
import { containsBlacklistedWords } from '@src/services/blackword';
import {
  deleteDataLocalStorage,
  getDataLocalStorage,
  setDataLocalStorage,
} from '@src/utils/localStorage';
import debounce from '@src/utils/debounce';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import { canUseEOLVoice, checkAdvancedGlobalVoice } from '@src/services/voice';
import { checkCustomPackage, checkFreePackage } from '@src/services/package';
import useUserInfo from '@src/hooks/useTtsUser';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';

import useLocationInfo from '@src/hooks/useLocation';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import {
  StyledActionWarning,
  StyledBlackWord,
  StyledMobileTTS,
} from './index.styled';
import SampleScripts from '../TTSNew/SampleScripts';
import AudioPlayer from './AudioPlayer';
import OutOfCreditsStandard from '../TTSNew/OutOfCreditsAlert/OutOfCreditsStandard';
import OutOfCreditsSpecial from '../TTSNew/OutOfCreditsAlert/OutOfCreditsSpecial';
import OutOfCreditsFree from '../TTSNew/OutOfCreditsAlert/OutOfCreditsFree';
import PaidMaxCharsLimitExceeded from '../TTSNew/MaxCharsLimitExceeded/PaidMaxCharsLimitExceeded';
import FreeMaxCharsLimitExceeded from '../TTSNew/MaxCharsLimitExceeded/FreeMaxCharsLimitExceeded';
import ExpiredPackageAlert from '../TTSNew/ExpiredPackageAlert';
import OutOfCreditsVip from '../TTSNew/OutOfCreditsAlert/OutOfCreditsVip';
import OutOfCreditsCustom from '../TTSNew/OutOfCreditsAlert/OutOfCreditsCustom';

const BUTTON_STATUS = {
  LOADING: 'loading',
  PROCESSED: 'processed',
  PLAYING: 'playing',
  PAUSE: 'pause',
  CREATE: 'create',
};

const STYLE_CONTENT_RELOAD = {
  color: '#1C1C1E',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  wordWrap: 'break-word',
  padding: '8px 0px',
  margin: '0px -4px',
};

const DATA_DRAFT_MOBILE = 'dataDraftMobile';

const MobileTTS = () => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isVietNam = checkVietNam();
  const audioRef = createRef();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { getFeatureValue } = useFeatureFlags();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const user = useSelector((state) => state.auth.user);
  const isSMSUser = Object.values(SMS_IDENTITY_PROVIDER).includes(
    user.identityProvider,
  );

  const dispatch = useDispatch();

  const [sampleScripts, setSampleScripts] = useState([]);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  const [defaultSampleScript, setDefaultSampleScript] = useState(null);
  const [enableEditor, setEnableEditor] = useState(true);
  // TODO: update state name => openWarningDialog
  const [openWrongSyntaxDialog, setOpenWrongSyntaxDialog] = useState(null);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [blackWords, setBlackWords] = useState([]);
  const [previewPayload, setPreviewPayload] = useState({});
  const [highlightedText, setHighlightedText] = useState('');
  const [hidePreviewDescription, setHidePreviewDescription] = useState(
    () => localStorage.getItem(HIDE_PREVIEW_DESCRIPTION) === 'true',
  );
  const [actionDialog, setActionDialog] = useState();
  const [notificationDialogV3, setNotificationDialogV3] = useState(null);
  const [remainingPreview, setRemainingPreview] = useState(null);
  const [usedFreePreviews, setUsedFreePreviews] = useState(0);

  const [buttonStatus, setButtonStatus] = useState(BUTTON_STATUS.CREATE);
  // State to track initial load
  const [initialLoad, setInitialLoad] = useState(true);
  const [openDraft, setOpenDraft] = useState(false);
  const [loadDraft, setLoadDraft] = useState(false);
  const [charsFactorCheckbox, setCharsFactorCheckbox] = useState(false);
  const [openOutOfCreditsDialog, setOpenOutOfCreditsDialog] = useState(false);
  const [openMaxCharsLimitExceeded, setOpenMaxCharsLimitExceeded] =
    useState(false);
  const [openExpiredPackageDialog, setOpenExpiredPackageDialog] =
    useState(false);
  const [warningOneTimeCredits, setWarningOneTimeCredits] = useState(false);

  const ttsRef = useRef(null);
  const handleChangeLoadDraft = () => {
    setLoadDraft(true);
    setOpenDraft(false);
  };

  const { keycloak } = useCustomKeycloak();

  const growthbook = useGrowthBook();
  const {
    synthesisRequest,
    voice,
    paragraphs,
    openSentences,
    sentences,
    showSample,
    defaultSample,
    advanceFeature,
  } = useSelector((state) => state.synthesisRequest);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const {
    user: { remainingCharacters, bonusCharacters, packageExpiryDate },
  } = useSelector((state) => state.auth);
  const { cycle, topUp, custom } = studio || {};
  let {
    remainingCredits: remainingCycleCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCycleCreditLocked,
  } = cycle || {};
  let {
    remainingCredits: remainingTopUpCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isTopUpCreditLocked,
  } = topUp || {};
  let {
    remainingCredits: remainingCustomCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCustomCreditLocked,
  } = custom || {};

  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const {
    isPlaying,
    requestPreviewId,
    audioType: playAudioType,
    showAudioPlayer,
    isAudioLoading,
  } = useSelector((state) => state.audioPlayer);
  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;
  const { filterVoice } = useSelector((state) => state.voice);
  const { languages } = useSelector((state) => state.language);
  const { ip } = useLocationInfo();

  const [viewportHeight, setViewportHeight] = useState(
    window.visualViewport?.height || window.innerHeight,
  );

  const inputLength = useMemo(() => {
    const lengthContent = countTextLength(synthesisRequest?.text || '');
    return lengthContent || 0;
  }, [synthesisRequest.text]);

  const ws = useRef(null);
  const currentReqPreviewId = useRef(requestPreviewId);
  const runStreamAudioRef = useRef(false);
  const currentIsPlayingAudio = useRef(null);
  const textareaRef = useRef(null);
  const packageCode = usingPackage?.code;

  const countCredits = useMemo(() => {
    const countLength = countCreditsSynthesis({
      text: synthesisRequest?.text,
      voice,
    });

    return countLength;
  }, [voice, synthesisRequest?.text]);

  const languageDefault =
    language === 'vi'
      ? languages.find((lang) => lang.value === 'vi-VN')
      : languages.find((lang) => lang.value === 'en-US');

  const handleUpgradeNow = () => history.push(route.PAYMENT);

  const handleClose = () => setOpenWrongSyntaxDialog(false);

  const handleCloseV2 = () => setOpenWrongSyntaxDialogV2(null);

  const handleCloseActionDialog = () => setActionDialog();

  const handleChangeButtonStatus = (status) => setButtonStatus(status);

  const handleCloseOutOfCreditsDialog = () => setOpenOutOfCreditsDialog(false);

  const handleCloseMaxCharsLimitExceededDialog = () =>
    setOpenMaxCharsLimitExceeded(false);

  const handleCloseExpiredPackageDialog = () => {
    setOpenExpiredPackageDialog(false);
  };

  const renderLockedCycleButton = () => (
    <Button variant="contained" onClick={() => setOpenDialogLockedCycle(null)}>
      {t('understoodV3')}
    </Button>
  );

  const getHighlightedText = () => {
    // Fix bug when select text on mobile device IOS
    // Need to wait for the selection to be completed
    setTimeout(() => {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      let selectedText = textarea.value.substring(start, end);
      selectedText =
        requestPreviewId && !selectedText ? previewPayload.text : selectedText;
      if (!isAudioLoading) setHighlightedText(selectedText);
    }, 500);
  };

  const handleHidePreviewDescription = () => {
    setHidePreviewDescription(true);
    localStorage.setItem(HIDE_PREVIEW_DESCRIPTION, 'true');
  };

  // Handle insert break time tag
  const insertBreakTime = (breakTime) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;

    const breakTag = `<break time=${breakTime}s/>`;

    const beforeText = synthesisRequest.text?.substring(0, start) || '';
    const afterText = synthesisRequest.text?.substring(end) || '';
    const updatedText = beforeText + breakTag + afterText;

    const dataSynthesis = {
      ...synthesisRequest,
      text: updatedText,
      status: '',
    };
    dispatch(actions.synthesisRequest.updateSynthesisConfig(dataSynthesis));

    if (osName === 'Android') {
      // Focuss to textarea and set cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + breakTag.length;
        textarea.selectionEnd = start + breakTag.length;
      }, 0);
    }
  };

  // Handle remove break time tag
  const handleKeyDown = (e) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;

    const breakTagRegex = /<break time=\d+s\/>/g;
    let match;

    // eslint-disable-next-line no-cond-assign
    while ((match = breakTagRegex.exec(synthesisRequest.text)) !== null) {
      const matchStart = match.index;
      const matchEnd = matchStart + match[0].length;

      if (start > matchStart && start <= matchEnd) {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          const updatedText =
            synthesisRequest.text.substring(0, matchStart) +
            synthesisRequest.text.substring(matchEnd);

          const dataSynthesis = {
            ...synthesisRequest,
            text: updatedText,
            status: '',
          };
          dispatch(
            actions.synthesisRequest.updateSynthesisConfig(dataSynthesis),
          );

          // Focuss to textarea and set cursor position
          setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = matchStart;
            textarea.selectionEnd = matchStart;
          }, 0);
          break;
        }
      }
    }
  };

  const handleClickBuyPackage = () => {
    history.push(route.PAYMENT);
  };

  const renderCloseButton = () => (
    <Button className="close-button" variant="contained" onClick={handleClose}>
      {t('understood')}
    </Button>
  );

  const renderCloseButtonV2 = () => (
    <Button
      className="btn-understood"
      variant="contained"
      onClick={handleClickBuyPackage}
    >
      {t('buyPackages')}
    </Button>
  );

  const renderNotificationDialogV3 = (handleCloseDialog) => {
    switch (openWrongSyntaxDialogV2?.type) {
      case NOTIFICATION_DIALOG_V3_TYPE.CANNOT_USE_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={handleCloseDialog}
          >
            {t('gotItV2')}
          </Button>
        );
      case DIALOG_V2_TYPE.RENAME_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setNotificationDialogV3(null);
            }}
          >
            {t('gotIt')}
          </Button>
        );
      default:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={handleCloseDialog}
          >
            {t('gotItV2')}
          </Button>
        );
    }
  };

  const showWarningCannotUseVoiceDialog = () => {
    setNotificationDialogV3({
      name: 'error-syntax',
      variant: 'warning',
      title: t('notice'),
      description: sanitizeHtml(
        t('eolVoiceoverHasBeenDiscontinued', { voiceName: voice.name }),
        {
          allowedTags: ['p', 'strong'],
          allowedAttributes: {
            '*': ['style'],
          },
        },
      ),
      type: NOTIFICATION_DIALOG_V3_TYPE.CANNOT_USE_VOICE,
    });
  };

  const renderUpgradeButton = () => (
    <Button onClick={handleUpgradeNow} variant="contained">
      {t('upgradeNow')}
    </Button>
  );

  const handleChangeOpenWrongSyntaxDialog = ({ dialogInfo, actionType }) => {
    setOpenWrongSyntaxDialog({
      ...dialogInfo,
      actionComponent:
        actionType === 'upgradeNow'
          ? renderUpgradeButton()
          : renderCloseButton(),
    });
  };

  const handleChangeText = (event) => {
    if (isPlaying) {
      handleChangeOpenWrongSyntaxDialog({
        dialogInfo: {
          name: 'checkCanConfig',
          variant: 'warning',
          title: t('note'),
          description: t('notePauseAudio'),
        },
      });
      return;
    }
    const dataSynthesis = {
      ...synthesisRequest,
      text: event.target.value,
      status: '',
    };
    dispatch(actions.synthesisRequest.updateSynthesisConfig(dataSynthesis));
  };

  const handleEnableEditor = (value) => setEnableEditor(value);

  const useUserEolDate = getFeatureValue(
    FEATURE_KEYS.UPDATE_VOICES_BY_USER_EOL_DATE,
    {
      userId: user.id,
    },
  );
  const voiceHasBeenRenamed = getFeatureValue(
    FEATURE_KEYS.VOICE_HAS_BEEN_RENAMED,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber, ip },
  );

  const canUserUseVoiceCloning =
    getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    }) ||
    (validateUserPackageCanUseVoiceCloning(ttsUser) &&
      ttsUser?.packageExpiryDate &&
      moment().isBefore(ttsUser?.packageExpiryDate));

  const handleCloseNotificationDialogV2 = () =>
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const renderButtonUnderstand = () => (
    <Button
      variant="contained"
      onClick={handleCloseNotificationDialogV2}
      sx={{
        float: 'right',
        marginLeft: 'auto',
        padding: '8px 48px',
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 700,
      }}
    >
      {t('gotIt')}
    </Button>
  );

  // show for voice cloning upgrade
  const handleShowDialogUpgrade = () =>
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('voiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        onUpgrade: () => {
          history.push(
            `${route.PAYMENT}?package=${PACKAGE_CODE.STUDIO_ENTERPRISE_YEAR_V2}`,
          );
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );

  const handleLoadContentEditor = (
    synthesisRequestDataFromLocalStorage,
    sentencesSave,
    isAdvanceFeature,
    isEnableEditor,
  ) => {
    setEnableEditor(isEnableEditor);
    if (!isAdvanceFeature) {
      const sentencesText = sentencesSave.map((item) => item.text);
      const paragraphText = sentencesText.join('\n');

      const dataSynthesis = {
        ...synthesisRequest,
        ...synthesisRequestDataFromLocalStorage,
        text: paragraphText,
      };

      dispatch(actions.synthesisRequest.updateSynthesisConfig(dataSynthesis));
    }
    if (isAdvanceFeature) {
      dispatch(actions.synthesisRequest.switchAdvanceFeature(true));
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest(
          'paragraphs',
          sentencesSave,
        ),
      );
    }
    setLoadDraft(false);
  };

  const handleLoadDataDraft = () => {
    const data = getDataLocalStorage(DATA_DRAFT_MOBILE);
    if (!data) return;
    // Load Voice Type
    dispatch(actions.synthesisRequest.updateVoice(data.voice));

    // Load setting of voice type
    const synthesisRequestSave = data.synthesisRequest;
    Object.keys(synthesisRequestSave).map((key) =>
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest(
          key,
          synthesisRequestSave[key],
        ),
      ),
    );
    // Load content edit save
    handleLoadContentEditor(
      data.synthesisRequest,
      data.sentencesSave,
      data.advanceFeature,
      data.enableEditor,
    );
  };

  const handleSaveDraft = () => {
    if (!inputLength) return;
    let sentencesSave;
    if (advanceFeature) sentencesSave = paragraphs;

    if (!advanceFeature) {
      if (openSentences) {
        sentencesSave = sentences;
      } else {
        sentencesSave = splitParagraphIntoSentence(
          synthesisRequest.text,
          voice,
        );
      }
    }
    const dataDraft = {
      advanceFeature,
      openSentences,
      inputLength,
      synthesisRequest,
      sentencesSave,
      voice,
      enableEditor,
    };

    setDataLocalStorage(
      DATA_DRAFT_MOBILE,
      dataDraft,
      LOCAL_STORAGE_EXPIRES_TIME,
    );
  };

  const handleCloseDraft = () => {
    setOpenDraft(false);
    setLoadDraft(false);
    deleteDataLocalStorage(DATA_DRAFT_MOBILE);
  };

  const processDataDraft = () => {
    if (initialLoad) {
      setInitialLoad(false);
      return; // Skip processing on the first load
    }

    if (inputLength) {
      handleSaveDraft();
    } else {
      deleteDataLocalStorage(DATA_DRAFT_MOBILE);
    }
  };

  const fetchBlackWords = async () => {
    const data = await apis.blackWords.getBlackWords();
    if (data.result) {
      const blackWordsData = data.result.blackWords;
      setBlackWords(
        blackWordsData.map((blackWord) => blackWord.word?.toLowerCase()),
      );
    }
  };

  const fetchSampleScript = async () => {
    const response = await apis.sampleScripts.getSampleScript();
    const totalSampleScript = response?.result?.sampleScripts;
    let sampleScript = [];
    if (isVietNam)
      sampleScript = totalSampleScript.filter(
        (item) => item.isVietnamese && !item.isDefault,
      );
    else
      sampleScript = totalSampleScript.filter(
        (item) => !item.isVietnamese && !item.isDefault,
      );
    setSampleScripts(sampleScript);

    const defaultItem = totalSampleScript.find(
      (item) =>
        item.isDefault && (isVietNam ? item.isVietnamese : !item.isVietnamese),
    );
    setDefaultSampleScript(defaultItem);
    dispatch(actions.synthesisRequest.updateDefaultSample(defaultItem));
  };

  const placeholder = useMemo(
    () => defaultSampleScript?.text || defaultSample?.text || '',
    [defaultSampleScript, defaultSample],
  );

  const fetchVoicesByViLanguage = async () => {
    const data = await apis.voices.getVoices({
      languageCode: LANGUAGE_CODE.VIETNAMESE,
      active: true,
    });
    if (data.result) {
      const vietnameseVoices = data.result.voices;

      if (vietnameseVoices.length) {
        // setVoice(vietnameseVoices[0]);
        dispatch(actions.synthesisRequest.updateVoice(vietnameseVoices[0]));
      }
    }
  };

  const fetchVoicesByEnglishLanguage = async () => {
    const data = await apis.voices.getVoices({
      languageCode: ENGLISH_VOICE_CODES.toString(),
      active: true,
      sort: 'rank_asc',
    });
    if (data.result) {
      const englishVoices = data.result.voices;
      if (englishVoices.length) {
        // setVoice(englishVoices[0]);
        dispatch(actions.synthesisRequest.updateVoice(englishVoices[0]));
        dispatch(actions.voice.setDefaultGlobalVoice(englishVoices[0].code));
      }
    }
  };

  const getVoiceDefault = async () => {
    if (Object.keys(voice).length) return;
    const isUseFreePackage = user.packageCode === PACKAGE_CODE.STUDIO_FREE;
    if (isVietNam || isUseFreePackage || isSMSUser) fetchVoicesByViLanguage();
    else fetchVoicesByEnglishLanguage();
  };

  const removePreviewAudio = () => {
    dispatch(actions.audioPlayer.updateShowAudioPlayer(false));
    dispatch(actions.audioPlayer.updateAudioLink(''));
    dispatch(actions.audioPlayer.updateSelectedAudioRequest(''));
    dispatch(actions.audioPlayer.updateStatus(false));
    dispatch(actions.audioPlayer.updateRequestPreviewId(''));
    dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
    setHighlightedText('');
    setPreviewPayload({});
  };

  const handleChangeVoice = (value, loadingSynthesisConfig) => {
    // Update with normal voice
    if (synthesisRequest?.voice?.code !== value?.code && !enableEditor) {
      setEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest());
    }
    dispatch(actions.synthesisRequest.updateSynthesisRequest('status', ''));
    dispatch(actions.synthesisRequest.updateVoice(value));

    removePreviewAudio();

    // if (
    //   value?.version !== TTS_VERSION.EMPHASIS &&
    //   voice?.version !== TTS_VERSION.EMPHASIS
    // )
    //   return;

    if (
      value?.code !== voice?.code &&
      !openSentences &&
      !loadingSynthesisConfig
    ) {
      let newParagraphs = [];
      let convertText = '';
      const isAdvanceFeature = value?.version === TTS_VERSION.EMPHASIS;
      if (isAdvanceFeature) {
        convertText = synthesisRequest?.text
          ?.getCurrentContent()
          ?.getPlainText('\n');
        newParagraphs = convertTextToParagraphs(convertText);
      } else {
        const text =
          convertParagraphsToText(paragraphs) || synthesisRequest.text;
        convertText = text
          ?.replace(REGEX.EMPHASIS_TAG, '')
          ?.replace(
            REGEX.ADVANCE_BREAK_TIME_TAG,
            (p1, p2) => `<break time=${p2}s/>`,
          );
      }

      const { id, status, createdAt, endedAt, progress, audioLink, ...rest } =
        synthesisRequest;
      const newSynthesisRequest =
        synthesisRequest?.voice?.code !== value?.code ? rest : synthesisRequest;

      const newSynthesisConfig = {
        ...newSynthesisRequest,
        paragraphs: newParagraphs,
        text: convertText,
        voice: value,
        characters: inputLength,
        sentences: [],
      };

      dispatch(
        actions.synthesisRequest.updateSynthesisConfig(newSynthesisConfig),
      );
      dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(true));
    }

    // Update with emphasis voice
    if (synthesisRequest?.voice?.code !== value?.code && !enableEditor) {
      setEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest());
    }
  };

  const checkVoicePermission = () => {
    const isDefaultGlobalVoice =
      !isVietNam &&
      usingPackage?.level === PACKAGE_LEVEL.BASIC &&
      voice?.code === defaultGlobalVoice;

    return (
      !voice?.features?.length ||
      isDefaultGlobalVoice ||
      voice.features.every((item) =>
        checkFeaturePermission(ttsUser.features, item),
      ) ||
      (canUseEOLVoice({ voice, user }) && useUserEolDate)
    );
  };

  const checkOutputAudioTypePermission = () => {
    const isPackageV2 =
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2 ||
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2_1;
    if (!isPackageV2) return true;
    const ouputAudio = OUTPUT_AUDIO.find(
      (item) => item.type === synthesisRequest?.audioType,
    );
    const hasFeature = checkFeaturePermission(
      ttsUser?.features,
      ouputAudio?.feature,
    );
    return hasFeature;
  };

  const checkValidCreateSynthesis = () => {
    const useLockOneTimeCreditsAdvancedVoice = getFeatureValue(
      FEATURE_KEYS.LOCK_ONE_TIME_CREDITS_ADVANCED_VOICE,
      { userId: user?.id, voiceCode: voice?.code, voiceName: voice?.name },
    );
    const isRenameVoice =
      voiceHasBeenRenamed && !voice?.active && !!voice?.canonicalVoice?.name;

    if (isRenameVoice) {
      setNotificationDialogV3({
        name: 'error-syntax',
        variant: 'none',
        title: t('notice'),
        description: sanitizeHtml(
          t('voiceHasBeenRenamed', {
            newName: synthesisRequest?.voice?.canonicalVoice?.name,
          }),
          {
            allowedTags: ['strong'],
          },
        ),
        type: DIALOG_V2_TYPE.RENAME_VOICE,
      });
      return false;
    }

    const canUseVoice = checkVoicePermission();
    if (!canUseVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
        }),
      );
      return false;
    }

    if (!countCredits) return false;

    const countLength = countTextLength(synthesisRequest.text);

    if (countLength > maxLengthInputText) {
      setOpenMaxCharsLimitExceeded(true);
      return false;
    }

    const isExpiredPackage =
      packageExpiryDate && moment().isAfter(packageExpiryDate);

    if (isExpiredPackage) {
      setOpenExpiredPackageDialog(true);
      return false;
    }

    const isNotUsingPackage = !usingPackage.id;
    if (isNotUsingPackage) {
      setOpenWrongSyntaxDialog({
        name: 'noPackage',
        variant: 'warning',
        title: t('noPackage'),
        description: t('noteNoPackage'),
        actionComponent: renderUpgradeButton(),
      });

      return false;
    }

    // Check lock cycle, show lock cycle dialog when only have cycle credits
    const isLockedCycle =
      isCycleCreditLocked &&
      !remainingTopUpCredits &&
      !remainingCharacters &&
      !bonusCharacters;

    const isLockedTopUp =
      isTopUpCreditLocked && !remainingCharacters && !bonusCharacters;

    if (isLockedCycle || isLockedTopUp) {
      setOpenDialogLockedCycle({
        title: t('charactersTemporarilyLocked'),
        description: t('descriptionPopupLockedCredit'),
      });
      return false;
    }

    if (isCycleCreditLocked) remainingCycleCredits = 0;
    if (isTopUpCreditLocked) remainingTopUpCredits = 0;
    if (isCustomCreditLocked) remainingCustomCredits = 0;

    let usageRemainingCharacters = remainingCharacters;
    const isAdvancedGlobalVoice = checkAdvancedGlobalVoice(voice);

    if (isAdvancedGlobalVoice && useLockOneTimeCreditsAdvancedVoice)
      usageRemainingCharacters = 0;

    const totalCredits =
      usageRemainingCharacters +
      bonusCharacters +
      remainingCycleCredits +
      remainingTopUpCredits +
      remainingCustomCredits;
    if (countCredits > totalCredits) {
      if (remainingCharacters > 0 && usageRemainingCharacters === 0)
        setWarningOneTimeCredits(true);
      setOpenOutOfCreditsDialog(true);
      return false;
    }

    const { speed } = synthesisRequest;

    const invalidSpeed = speed < VALID_SPEED.min || speed > VALID_SPEED.max;
    if (invalidSpeed) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'speedInvalidRange',
          value: t('range', {
            min: `${VALID_SPEED.min}x`,
            max: `${VALID_SPEED.max}x`,
          }),
        }),
      );
      return false;
    }

    return true;
  };

  const showNonVbeeWarningDialog = () => {
    setOpenWrongSyntaxDialogV2({
      name: 'error-syntax',
      variant: 'warning',
      title: t('note'),

      type: DIALOG_V2_TYPE.NON_VBEE_WARNING,
    });
  };

  const showCharsFactorWarningDialog = () => {
    const generateVoiceSummary = () => {
      const voiceName = voice.name;
      const voiceLevel = t(voice.level);
      const isBeta = voice.beta ? ' - Beta' : '';
      const factor = voice.creditFactor || 1;
      const voiceList = t('voiceFactor', {
        voiceName,
        voiceLevel,
        isBeta,
        factor,
      });

      return t('totalFactorCredits', {
        voiceList,
        countCredits: countCredits?.toLocaleString(language),
      });
    };

    const highCreditFactorVoices = sentences
      .filter((sentence) => sentence?.voice?.creditFactor > 1)
      .map((sentence) => sentence?.voice);

    const highCreditFactorVoicesUnique = highCreditFactorVoices.filter(
      (currVoice, index, self) =>
        index ===
        self.findIndex(
          (tVoice) =>
            tVoice.code === currVoice.code &&
            tVoice.provider === currVoice.provider,
        ),
    );
    setOpenWrongSyntaxDialogV2({
      name: 'error-syntax',
      variant: 'warning',
      title: t('note'),
      description: generateVoiceSummary(highCreditFactorVoicesUnique),
      type: DIALOG_V2_TYPE.NON_VBEE_WARNING,
    });
  };

  const checkUseNonVbeeVoice = () => {
    if (!openSentences && NON_VBEE_VOICE_PROVIDERS.includes(voice?.provider))
      return true;

    const isUseNonVbeeVoice = sentences.some((sentence) =>
      NON_VBEE_VOICE_PROVIDERS.includes(sentence?.voice?.provider),
    );
    if (isUseNonVbeeVoice) return true;

    return false;
  };

  const checkVoiceFactorGreaterThanOne = () => {
    const voiceCredits = voice?.creditFactor || 1;
    if (!openSentences && voiceCredits > 1) return true;

    const isVoiceFactorGreaterThanOne = sentences.some(
      (sentence) => (sentence?.voice?.creditFactor || 1) > 1,
    );
    if (isVoiceFactorGreaterThanOne && openSentences) return true;

    return false;
  };

  const createSynthesisRequest = async () => {
    const { title, audioType, bitrate, backgroundMusic, volume } =
      synthesisRequest;

    let synthesisPayload = { audioType, volume };

    // Update audio type to mp3 if not have permission (Mobile web)
    const canOutputAudioType = checkOutputAudioTypePermission();
    if (!canOutputAudioType) {
      synthesisPayload.audioType = 'mp3';
    }

    synthesisPayload.bitrate = bitrate || BITRATE[BITRATE.length - 1];

    if (title) synthesisPayload.title = title.trim();

    const hasBackgroundMusic =
      backgroundMusic && Object.keys(backgroundMusic).length;
    if (hasBackgroundMusic) {
      const { link, name, volume: bgVolume } = backgroundMusic;
      synthesisPayload.backgroundMusic = { link, name, volume: bgVolume };
    }

    const { text } = synthesisRequest;
    const hasZeroBreakTime = ZERO_BREAK_TIME_REGEX.test(text);
    if (hasZeroBreakTime) {
      // handleChangeOpenWrongBreakTimeDialog(true);
      return;
    }

    const plainText = text
      .replace(REGEX.ADVANCE_TAG, '')
      .replace(BREAK_TIME_REGEX, ' ')
      .replace(/  +/g, ' ');
    const blackWordsInText = blackWords.filter((blackWord) =>
      containsBlacklistedWords({
        text: plainText,
        blackWord,
      }),
    );

    const MAX_BLACK_WORDS_DISPLAY = 3;
    const slicedBlackWords = blackWordsInText.slice(0, MAX_BLACK_WORDS_DISPLAY);
    let blackWordsHtml = ReactDOMServer.renderToStaticMarkup(
      slicedBlackWords.map((blackWord) => (
        <StyledBlackWord key={blackWord}>{blackWord}</StyledBlackWord>
      )),
    );

    if (slicedBlackWords.length >= MAX_BLACK_WORDS_DISPLAY)
      blackWordsHtml += ', ...';

    if (blackWordsInText.length > 0) {
      apis.warning.sendWarningInvalidRequest({
        text,
        invalidTexts: blackWordsInText,
        type: WARNING_REQUEST_TYPE.BLACK_WORD,
      });

      setOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('sentenceHasBlackWords'),
        description: `
          ${t('inAppropriateWords')}
          (${blackWordsHtml}).
          ${t('invalidWordsDesc')}
        `,
      });
      return;
    }

    if (voice?.provider === VOICE_PROVIDER.VBEE) {
      const invalidTexts = text.match(VALID_CHARACTERS_LENGTH_REGEX) || [];
      const isValidText = VALID_CHARACTERS_REGEX.test(text);
      if (!isValidText) invalidTexts.push(text);

      if (invalidTexts?.length) {
        const invalidText = invalidTexts.join(', ');
        setOpenWrongSyntaxDialog({
          name: 'error-syntax',
          variant: 'warning',
          title: t('errorSyntax'),
          description: t('errorSyntaxDescription'),
          subDescription: t('invalidCharacter', { invalidText }),
        });
        apis.warning.sendWarningInvalidRequest({
          text,
          invalidTexts,
          type: WARNING_REQUEST_TYPE.CHARACTER_EXCEED,
        });
        return;
      }
    }

    synthesisPayload = {
      ...synthesisPayload,
      text,
      voiceCode: voice?.code,
      speed: Number(synthesisRequest.speed),
    };

    // setLoading(false);

    // const isStandardData = getFeatureValue(
    //   FEATURE_KEYS.STANDARDIZATION_USER_DATA_MOE,
    // );
    // if (!isStandardData)
    //   window.dataLayer.push({
    //     event: EVENT_TYPE.STUDIO_CONVERT_TTS,
    //     userId: user?.id,
    //     email: user.email,
    //     requestType: 'requestTTS',
    //     voice: voice?.name,
    //     characters: text.length,
    //     package: user.packageCode,
    //     remainingCharacters: user.remainingCharacters,
    //     packageExpiryDate: moment(user.packageExpiryDate).format(
    //       'DD/MM/YYY - HH:mm',
    //     ),
    //   });

    const options = {};
    const recaptchaTtsType = growthbook.getFeatureValue(
      FEATURE_KEYS.RECAPTCHA_TTS_TYPE,
    );

    let recaptchaToken;
    switch (recaptchaTtsType) {
      case RECAPTCHA_TYPE.V3:
        if (executeRecaptcha)
          recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION.SYNTHESIS);
        break;
      case RECAPTCHA_TYPE.ENTERPRISE:
        if (window.grecaptcha) {
          recaptchaToken = await window?.grecaptcha?.enterprise?.execute(
            RECAPTCHA_ENTERPRISE_SITE_KEY,
          );
        }
        break;
      default:
        break;
    }
    if (recaptchaToken) options.recaptchaToken = recaptchaToken;

    synthesisPayload.datasenses = datasenses.getDataSensesRequireFields();
    const data = await apis.synthesis.createSynthesis(
      synthesisPayload,
      options,
    );

    datasenses.sendMakeRequestEvent({
      userId: user.id,
      requestType: REQUEST_TYPE.STUDIO,
      voice: voice?.code,
      characters: text.length,
      status: REQUEST_STATUS.IN_PROGRESS,
    });

    const { bitrate: bitrateResponse } = data.result || {};

    const newSynthesisRequest = {
      ...data.result,
      bitrate:
        bitrateResponse % 1000 === 0 ? bitrateResponse / 1000 : bitrateResponse,
      id: data.result?.requestId,
    };
    dispatch(
      actions.synthesisRequest.updateSynthesisConfig(newSynthesisRequest),
    );
    // handleEnableEditor(false);
    // setLoading(false);
    if (data?.status) {
      // handleChangeRequestLoading(true);
      // document.getElementById('requests').scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'end',
      //   inline: 'nearest',
      // });
      history.push(route.MOBILE_REQUESTS);
    } else {
      const { errorMessage, errorCode } = data;

      switch (errorCode) {
        case ERROR_CODE.INVALID_SYNTAX: {
          setOpenWrongSyntaxDialog({
            name: 'error-syntax',
            variant: 'warning',
            title: t('errorSyntax'),
            description: t('errorSyntaxDescription'),
          });
          break;
        }

        case ERROR_CODE.SPAM_EMAIL: {
          setOpenWrongSyntaxDialogV2({
            name: 'error-syntax',
            variant: 'warning',
            title: t('cannotConvertTTS'),
            description: t('blockSpamEmailDescV2'),
          });
          break;
        }

        case ERROR_CODE.INACTIVE_VOICE: {
          const voiceName = voice?.name;

          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', { voiceName }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.DISCARDED_VOICE_CLONING: {
          dispatch(
            actions.dialog.displayDialogV2({
              notificationDialogV2: {
                name: 'voice-cloning-discard-info',
                title: t('unavailableVoice'),
                description: t('discardedVoiceCloning'),
                open: true,
                actionComponent: renderButtonUnderstand(),
                showIcon: false,
                onClose: handleCloseNotificationDialogV2,
                variant: null,
              },
            }),
          );
          break;
        }

        case ERROR_CODE.NO_MORE_SUPPORT_VOICE: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', { voiceName: voice?.name }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.TEXT_TOO_LONG: {
          setOpenOutOfCreditsDialog(true);
          break;
        }

        default: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }
      }
    }
  };

  const handleCreateSynthesis = async (isDefaultText) => {
    if (isDefaultText) return;

    // Reset preview request id
    removePreviewAudio();

    if (isSameToday(user?.createdAt))
      setTimeout(
        () =>
          dispatch(
            actions.banner.updateDisplayBanner(
              BANNER_TYPE.FIRST_BUY_PACKAGE_TRIAL,
              true,
            ),
          ),
        POPUP_TIME_OUT,
      );

    if (!checkValidCreateSynthesis()) return;

    const notShowCharsFactorDialog = localStorage.getItem('charsFactorDialog');
    const notShowNonVbeeDialog = localStorage.getItem('nonVbeeDialog');

    if (!notShowCharsFactorDialog && checkVoiceFactorGreaterThanOne()) {
      showCharsFactorWarningDialog();
    } else if (voice?.creditFactor !== 1 && !notShowNonVbeeDialog) {
      const useNonVbeeVoice = checkUseNonVbeeVoice();
      if (useNonVbeeVoice) showNonVbeeWarningDialog();
      else await createSynthesisRequest();
    } else {
      await createSynthesisRequest();
    }
  };

  const handleClickCharsFactorCheckbox = () => {
    if (!charsFactorCheckbox) {
      localStorage.setItem('nonVbeeDialog', true);
      localStorage.setItem('charsFactorDialog', true);
    } else {
      localStorage.removeItem('nonVbeeDialog');
      localStorage.removeItem('charsFactorDialog');
    }
    setCharsFactorCheckbox((value) => !value);
  };

  const handleConfirmNonVbeeDialog = async () => {
    setOpenWrongSyntaxDialogV2(null);
    await createSynthesisRequest();
  };

  const renderActionPopupCharsFactor = () => (
    <div className="non-vbee-warning">
      <FormControlLabel
        control={
          <Checkbox
            classes={{ root: 'checkbox' }}
            color="iconPrimary"
            value={charsFactorCheckbox}
            onChange={handleClickCharsFactorCheckbox}
          />
        }
        label={
          <Typography className="not-show-again-text">
            {t('notShowAgain')}
          </Typography>
        }
        labelPlacement={t('notShowAgain')}
      />
      <div className="btn-group">
        <Button
          className="btn-non-vbee-warning cancel-btn"
          variant="outlined"
          onClick={handleCloseV2}
        >
          {t('cancel')}
        </Button>
        <Button
          className="btn-non-vbee-warning"
          variant="contained"
          onClick={handleConfirmNonVbeeDialog}
        >
          {t('convert')}
        </Button>
      </div>
    </div>
  );

  const renderActionsPopupV2 = () => {
    switch (openWrongSyntaxDialogV2?.type) {
      case DIALOG_V2_TYPE.NON_VBEE_WARNING:
        return renderActionPopupCharsFactor();

      default:
        return renderCloseButtonV2();
    }
  };

  const checkValidatePreviewSentence = (sentence) => {
    const { voice: sentenceVoice, speed } = sentence;
    const text = sentence?.text;

    const isExpiredPackage =
      packageExpiryDate && moment().isAfter(packageExpiryDate);

    if (isExpiredPackage) {
      setOpenExpiredPackageDialog(true);
      return { error: true };
    }

    if (!text || !text.trim()) {
      setOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('errorSyntax'),
        description: t('errorSyntaxDescription'),
      });
      return { error: true };
    }

    const plainText = text
      .replace(REGEX.ADVANCE_TAG, '')
      .replace(BREAK_TIME_REGEX, ' ')
      .replace(/  +/g, ' ');
    const blackWordsInText = blackWords.filter((blackWord) =>
      containsBlacklistedWords({
        text: plainText,
        blackWord,
      }),
    );
    if (blackWordsInText.length > 0) {
      apis.warning.sendWarningInvalidRequest({
        text,
        invalidTexts: blackWordsInText,
        type: WARNING_REQUEST_TYPE.BLACK_WORD,
      });

      setOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('sentenceHasBlackWords'),
        description: t('sentenceHasBlackWordsDescription'),
      });
      return { error: true };
    }

    if (sentenceVoice?.provider === VOICE_PROVIDER.VBEE) {
      const invalidTexts = text.match(VALID_CHARACTERS_LENGTH_REGEX) || [];
      const isValidText = VALID_CHARACTERS_REGEX.test(text);
      if (!isValidText) invalidTexts.push(text);

      if (invalidTexts?.length) {
        const invalidText = invalidTexts.join(', ');
        setOpenWrongSyntaxDialog({
          name: 'error-syntax',
          variant: 'warning',
          title: t('errorSyntax'),
          description: t('errorSyntaxDescription'),
          subDescription: t('invalidCharacter', { invalidText }),
        });
        apis.warning.sendWarningInvalidRequest({
          text,
          invalidTexts,
          type: WARNING_REQUEST_TYPE.CHARACTER_EXCEED,
        });
        return { error: true };
      }
    }

    const invalidSpeed = speed < VALID_SPEED.min || speed > VALID_SPEED.max;
    if (invalidSpeed) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'speedInvalidRange',
          value: t('range', {
            min: `${VALID_SPEED.min}x`,
            max: `${VALID_SPEED.max}x`,
          }),
        }),
      );
      return { error: true };
    }

    const countLength = countTextLength(text);
    const maxTryListeningCharacters =
      ttsUser.maxLengthDemoInput || MAX_TRY_LISTENING_CHARACTERS;
    if (!countLength || countLength > maxTryListeningCharacters) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'warningListening',
          value: maxTryListeningCharacters,
        }),
      );
      return { error: true };
    }
    return { error: false, previewData: { ...sentence, text } };
  };

  const handlePreviewSentence = (sentence = {}) => {
    const { error } = checkValidatePreviewSentence(sentence);
    const { text } = sentence;
    if (error) return;

    const { id: sentenceId, voice: sentenceVoice, speed } = sentence;
    dispatch(
      actions.audioPlayer.updatePreviewRequestInfo({
        voice,
        title: t('preview'),
      }),
    );
    dispatch(actions.audioPlayer.updateShowAudioPlayer(true));

    const checkEmphasisInText = REGEX.ADVANCE_TAG.test(text);
    const suitableVersion = checkEmphasisInText
      ? VOICE_VERSION.ADVANCE
      : VOICE_VERSION.NORMAL;

    dispatch(actions.audioPlayer.updateAudioLink(''));
    dispatch(actions.audioPlayer.updateSelectedAudioRequest(''));
    dispatch(actions.audioPlayer.updateStatus(false));
    dispatch(actions.audioPlayer.updateStatusStreamAudio(false));
    dispatch(
      actions.audioPlayer.initStreamAudios(
        suitableVersion || sentenceVoice?.version,
      ),
    );
    dispatch(actions.audioPlayer.updateAudioType(PLAYING_AUDIO_TYPE.SILENCE));
    dispatch(
      actions.audioPlayer.updateMetaData({ currentTime: 0, duration: 0 }),
    );
    dispatch(
      actions.audioPlayer.updateTryListeningSentence({
        isAudioLoading: true,
        sentenceId,
      }),
    );

    const { audioType, bitrate, backgroundMusic, volume } = synthesisRequest;

    const payload = {
      text,
      voiceCode: sentenceVoice?.code,
      speed,
      audioType,
      volume,
    };

    if (advanceFeature) {
      payload.bitrate = bitrate;
      payload.version = TTS_VERSION.EMPHASIS;
    } else {
      payload.bitrate = bitrate;
    }

    const hasBackgroundMusic =
      backgroundMusic && Object.keys(backgroundMusic).length;
    if (hasBackgroundMusic) {
      const { link, name, volume: bgVolume } = backgroundMusic;
      payload.backgroundMusic = { link, name, volume: bgVolume };
    }

    payload.datasenses = datasenses.getDataSensesRequireFields();
    setPreviewPayload(payload);
  };

  const handlePreviewTrialAction = (previewSentence) => {
    handlePreviewSentence(previewSentence);
    handleCloseActionDialog();
  };

  const checkConditionPackageToPreview = (sentence) => {
    const isRenameVoice =
      voiceHasBeenRenamed && !voice?.active && !!voice?.canonicalVoice?.name;

    if (isRenameVoice) {
      setNotificationDialogV3({
        name: 'error-syntax',
        variant: 'none',
        title: t('notice'),
        description: sanitizeHtml(
          t('voiceHasBeenRenamed', {
            newName: synthesisRequest?.voice?.canonicalVoice?.name,
          }),
          {
            allowedTags: ['strong'],
          },
        ),
        type: DIALOG_V2_TYPE.RENAME_VOICE,
      });
      return;
    }
    if (!ttsUser.features.includes(FEATURE.PREVIEW)) {
      setActionDialog({
        image: UpgradeImg,
        title: t('premiumFeature'),
        description: t('premiumFeatureDescription'),
        onClose: handleCloseActionDialog,
        actionComponents: renderUpgradeButton(),
      });
      return;
    }

    if (LIMITED_PREVIEW_PACKAGE_LEVEL.includes(usingPackage.level)) {
      if (!remainingPreview)
        setActionDialog({
          image: UpgradeImg,
          title: t('previewOver'),
          description: t('previewOverDescription'),
          onClose: handleCloseActionDialog,
          actionComponents: renderUpgradeButton(),
        });
      else {
        setActionDialog({
          image: TrialPreviewImage,
          title: t('previewTrial', { remainingPreview }),
          description: t('previewTrialDescription'),
          onClose: handleCloseActionDialog,
          actionComponents: (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCloseActionDialog}
              >
                {t('noValue')}
              </Button>
              <Button
                variant="contained"
                onClick={() => handlePreviewTrialAction(sentence)}
              >
                {t('preview')}
              </Button>
            </>
          ),
        });
      }
      return;
    }

    if (
      ttsUser?.packageCode &&
      checkFreePackage(ttsUser?.packageCode) &&
      ttsUser?.maxPreview &&
      usedFreePreviews >= ttsUser?.maxPreview
    ) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('trialEndedMessage'),
          img: CrownImg,
          classNameImg: 'custom-img',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    const canUseVoice = checkVoicePermission();
    if (!canUseVoice) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'limitedVoiceDescription',
        }),
      );
      return;
    }

    handlePreviewSentence(sentence);
  };

  const handleTryListening = () => {
    // validate if user can use voice cloning
    if (
      !canUserUseVoiceCloning &&
      voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
      voice?.userId === user?.id &&
      voice?.status === SUB_STATUS.PRIVATE
    ) {
      handleShowDialogUpgrade();
      return;
    }
    // Change button status to create
    handleChangeButtonStatus(BUTTON_STATUS.CREATE);

    if (isSameToday(user?.createdAt))
      setTimeout(
        () =>
          dispatch(
            actions.banner.updateDisplayBanner(
              BANNER_TYPE.FIRST_BUY_PACKAGE_TRIAL,
              true,
            ),
          ),
        POPUP_TIME_OUT,
      );

    // eslint-disable-next-line no-param-reassign
    audioRef.current.src = SILENCE_AUDIO_URL;
    audioRef.current.load();
    audioRef.current.play();

    const sentence = {
      text: highlightedText,
      voice,
      speed: synthesisRequest.speed,
    };

    checkConditionPackageToPreview(sentence);
  };

  const handleClickPlayPreviewButton = () => {
    // Check current preview text is the same as highlighted text
    if (requestPreviewId && highlightedText === previewPayload.text)
      dispatch(actions.audioPlayer.updateStatus(true));
    else handleTryListening();
  };

  const handlePausePreviewAudio = () => {
    dispatch(actions.audioPlayer.updateStatus(!isPlaying));
  };

  // check draft in mobile web
  useEffect(() => {
    const isInSameSession = queryParams.get('inSameSession') || '';
    const dataDraftMobile = getDataLocalStorage(DATA_DRAFT_MOBILE);

    if (isInSameSession) {
      const newUrlParams = new URLSearchParams(queryParams);
      newUrlParams.delete('inSameSession');

      history.replace({
        pathname: location.pathname,
        search: newUrlParams.toString(),
      });
      setInitialLoad(false);
    }

    if (dataDraftMobile && !isInSameSession) {
      setOpenDraft(true);
    }
  }, []);

  useEffect(() => {
    if (loadDraft) handleLoadDataDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (!openDraft) {
      debounce(processDataDraft, SET_TIME_AUTO_SAVE)();
    }
  }, [
    advanceFeature,
    openSentences,
    inputLength,
    sentences,
    voice,
    synthesisRequest,
    synthesisRequest?.status,
    paragraphs,
    initialLoad,
  ]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const updateViewportInfo = () =>
      setViewportHeight(window.visualViewport.height);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportInfo);
      window.visualViewport.addEventListener('scroll', updateViewportInfo);

      // Clean up event listeners on component unmount
      return () => {
        window.visualViewport.removeEventListener('resize', updateViewportInfo);
        window.visualViewport.removeEventListener('scroll', updateViewportInfo);
      };
    }
  }, []);

  const renderPreviewButton = useCallback(() => {
    const disabledPreview =
      highlightedText?.length === 0 || buttonStatus === BUTTON_STATUS.LOADING;

    const isDifferentText = highlightedText !== previewPayload.text;

    if (isPlaying && requestPreviewId && !isDifferentText)
      return (
        <LoadingButton
          variant="outlined"
          loading={!!isAudioLoading}
          loadingPosition="start"
          size="small"
          startIcon={<PauseRounded />}
          className="preview-button"
          onClick={handlePausePreviewAudio}
        >
          {t('preview')}
        </LoadingButton>
      );

    return (
      <LoadingButton
        disabled={disabledPreview}
        variant="outlined"
        loading={!!isAudioLoading}
        loadingPosition="start"
        size="small"
        startIcon={<PlayArrowRounded />}
        className="preview-button"
        onClick={handleClickPlayPreviewButton}
      >
        {t('preview')}
      </LoadingButton>
    );
  }, [
    audioRef,
    isAudioLoading,
    isPlaying,
    highlightedText,
    buttonStatus,
    requestPreviewId,
    previewPayload,
  ]);

  const renderConfirmLoadDraft = (handleCloseAction, handleConfirmAction) => (
    <StyledActionWarning>
      <Button
        className="cancel-btn"
        variant="outlined"
        onClick={handleCloseAction}
      >
        {t('cancel')}
      </Button>
      <Button
        className="agree-btn"
        variant="contained"
        onClick={handleConfirmAction}
      >
        {t('agree')}
      </Button>
    </StyledActionWarning>
  );

  useEffect(() => {
    fetchSampleScript();
  }, [isVietNam]);

  const onMobile = true;
  useEffect(() => {
    getVoiceDefault();
    fetchBlackWords();
    dispatch(
      actions.voice.changeFilterVoice({
        ...filterVoice,
        languageCode: languageDefault.value,
      }),
    );
  }, [languageDefault]);

  useEffect(() => {
    if (synthesisRequest.text?.length && showSample)
      dispatch(actions.synthesisRequest.updateShowSampleScripts(false));
  }, [synthesisRequest.text?.length]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= MOBILE_BREAKPOINT) history.push(route.TTS);
    };
    handleResize();
  }, []);

  const connectSynthesisWS = async () => {
    if (!previewPayload?.text) return;

    const recaptchaTryListeningTtsType = growthbook.getFeatureValue(
      FEATURE_KEYS.RECAPTCHA_TRY_LISTEN_TTS_TYPE,
    );

    let recaptchaToken;
    switch (recaptchaTryListeningTtsType) {
      case RECAPTCHA_TYPE.V3:
        if (executeRecaptcha)
          recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION.SYNTHESIS);
        break;
      case RECAPTCHA_TYPE.ENTERPRISE:
        if (window.grecaptcha) {
          recaptchaToken = await window?.grecaptcha?.enterprise?.execute(
            RECAPTCHA_ENTERPRISE_SITE_KEY,
          );
        }
        break;
      default:
        break;
    }

    ws.current = new WebSocket(TTS_SYNTHESIS_URL);
    let pingInterval;
    ws.current.onopen = () => {
      ws.current.send(
        JSON.stringify({
          type: WS_TYPE.INIT,
          accessToken: keycloak?.token,
        }),
      );
      pingInterval = setInterval(() => {
        ws.current.send(JSON.stringify({ type: WS_TYPE.PING }));
      }, PING_INTERVAL);
    };

    const handleTryListeningError = () => {
      const failurePayload = { sentenceId: '', isAudioLoading: false };
      dispatch(actions.audioPlayer.updateTryListeningSentence(failurePayload));
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'listeningError',
        }),
      );
      dispatch(actions.audioPlayer.updateRequestPreviewId(''));
    };

    ws.current.onmessage = (res) => {
      const responseData = camelcaseKeys(JSON.parse(res.data), { deep: true });
      const {
        type,
        result,
        status,
        remainingPreview: newRemainingPreview,
        usedFreePreviews: numberUsedFreePreviews = 0,
      } = responseData;

      if (status === 0) handleTryListeningError();
      if (type === WS_TYPE.INIT) {
        ws.current.send(
          JSON.stringify({
            type: WS_TYPE.SYNTHESIS,
            payload: previewPayload,
            accessToken: keycloak?.token,
            recaptchaToken,
          }),
        );

        // datasenses.sendPreviewEvent({
        //   userId: user.id,
        //   requestType: REQUEST_TYPE.STUDIO,
        //   voice: previewPayload.voiceCode,
        //   characters: previewPayload.text?.length || 0,
        //   status: REQUEST_STATUS.IN_PROGRESS,
        // });
      }
      if (type === WS_TYPE.SYNTHESIS) {
        switch (result && result.status) {
          case REQUEST_STATUS.IN_PROGRESS: {
            dispatch(
              actions.audioPlayer.updateRequestPreviewId(result.requestId),
            );
            break;
          }
          case REQUEST_STATUS.SUCCESS: {
            const getPublicAudioLink = async (requestId) => {
              const presignedAudioUrlResponse =
                await apis.requests.getPresignedAudioUrl(requestId);

              if (presignedAudioUrlResponse.status)
                return presignedAudioUrlResponse.result.audio;

              return null;
            };

            const playTryListeningAudio = async () => {
              ws.current.send(
                JSON.stringify({ type: WS_TYPE.GET_REMAINING_PREVIEW }),
              );
              if (currentReqPreviewId.current !== result.requestId) return;

              if (
                runStreamAudioRef.current &&
                checkFeaturePermission(
                  ttsUser.features,
                  FEATURE.STREAMING_PREVIEW,
                )
              ) {
                dispatch(
                  actions.audioPlayer.updateSelectedAudioRequest(
                    result?.requestId,
                  ),
                );
                dispatch(
                  actions.audioPlayer.updateFinalStreamAudio(result?.audioLink),
                );
                return;
              }

              if (
                currentIsPlayingAudio.current &&
                playAudioType === PLAYING_AUDIO_TYPE.SYNTHESIS
              ) {
                const cancelPayload = { sentenceId: '', isAudioLoading: false };
                dispatch(
                  actions.audioPlayer.updateTryListeningSentence(cancelPayload),
                );
              } else {
                const publicAudioLink = await getPublicAudioLink(
                  result?.requestId,
                );

                const audioLink = publicAudioLink || result?.audioLink;

                if (!showAudioPlayer)
                  dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
                dispatch(actions.audioPlayer.updateAudioLink(audioLink, true));
                dispatch(actions.audioPlayer.updateSelectedAudioRequest(''));
                dispatch(
                  actions.audioPlayer.updateAudioType(
                    PLAYING_AUDIO_TYPE.SYNTHESIS,
                  ),
                );
                dispatch(
                  actions.audioPlayer.updateTryListeningSentence({ audioLink }),
                );
                dispatch(actions.audioPlayer.updateStatus(true));
              }
              // dispatch(actions.audioPlayer.updateRequestPreviewId(''));
            };

            playTryListeningAudio();

            // disconnect websocket
            ws.current.close();
            break;
          }
          case REQUEST_STATUS.FAILURE: {
            handleTryListeningError();

            // disconnect websocket
            ws.current.close();
            break;
          }
          default:
            break;
        }
      }

      if (type === WS_TYPE.GET_REMAINING_PREVIEW) {
        setRemainingPreview(newRemainingPreview);
        setUsedFreePreviews(numberUsedFreePreviews);
      }
    };

    ws.current.onclose = (event) => {
      const { code, reason, wasClean } = event;
      // eslint-disable-next-line no-console
      console.log('Websocket is closed', { code, reason, wasClean });
      clearInterval(pingInterval);
    };
  };

  const connectGetRemainingPreviewWS = () => {
    ws.current = new WebSocket(TTS_SYNTHESIS_URL);
    let pingInterval;
    ws.current.onopen = () => {
      ws.current.send(
        JSON.stringify({
          type: WS_TYPE.INIT,
          accessToken: keycloak?.token,
        }),
      );
      pingInterval = setInterval(() => {
        ws.current.send(JSON.stringify({ type: WS_TYPE.PING }));
      }, PING_INTERVAL);
    };

    ws.current.onmessage = (res) => {
      const responseData = camelcaseKeys(JSON.parse(res.data), { deep: true });
      const {
        type,
        remainingPreview: newRemainingPreview,
        usedFreePreviews: numberUsedFreePreviews = 0,
      } = responseData;

      if (type === WS_TYPE.INIT)
        ws.current.send(
          JSON.stringify({ type: WS_TYPE.GET_REMAINING_PREVIEW }),
        );

      if (type === WS_TYPE.GET_REMAINING_PREVIEW) {
        setRemainingPreview(newRemainingPreview);
        setUsedFreePreviews(numberUsedFreePreviews);
      }
    };

    ws.current.onclose = (event) => {
      const { code, reason, wasClean } = event;
      // eslint-disable-next-line no-console
      console.log('Websocket is closed.', { event, code, reason, wasClean });
      clearInterval(pingInterval);
    };
  };

  const renderOutOfCreditsDialog = () => {
    const isPackageStandardV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.STANDARD &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageSpecialV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.SPECIAL &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageVipV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.VIP &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isCustomPackage = checkCustomPackage({
      type: usingPackage?.type,
      level: usingPackage.level,
    });

    if (isCustomPackage) {
      return (
        <OutOfCreditsCustom
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
        />
      );
    }

    if (isPackageStandardV3) {
      return (
        <OutOfCreditsStandard
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          voice={voice}
        />
      );
    }

    if (isPackageSpecialV3) {
      return (
        <OutOfCreditsSpecial
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          voice={voice}
        />
      );
    }

    if (isPackageVipV3)
      return (
        <OutOfCreditsVip
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          warningOneTimeCredits={warningOneTimeCredits}
        />
      );

    // Display this popup when using a free package or a package that is neither standard nor special v3.
    return (
      <OutOfCreditsFree
        open={openOutOfCreditsDialog}
        onClose={handleCloseOutOfCreditsDialog}
      />
    );
  };

  const renderMaxCharsLimitExceededDialog = () => {
    const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);

    if (isFreePackage) {
      return (
        <FreeMaxCharsLimitExceeded
          open={openMaxCharsLimitExceeded}
          onClose={handleCloseMaxCharsLimitExceededDialog}
          maxLengthInputText={maxLengthInputText}
        />
      );
    }

    return (
      <PaidMaxCharsLimitExceeded
        open={openMaxCharsLimitExceeded}
        onClose={handleCloseMaxCharsLimitExceededDialog}
        maxLengthInputText={maxLengthInputText}
      />
    );
  };

  useEffect(() => {
    connectGetRemainingPreviewWS();
  }, []);

  useEffect(() => {
    if (highlightedText && isAudioLoading) connectSynthesisWS();
  }, [isAudioLoading, highlightedText, previewPayload]);

  useEffect(() => {
    currentReqPreviewId.current = requestPreviewId;
  }, [requestPreviewId]);

  return (
    <StyledMobileTTS
      isTooManyChars={synthesisRequest?.text?.length > maxLengthInputText}
      isPlaying={isPlaying}
      ref={ttsRef}
      screenHeight={viewportHeight}
    >
      <div className="page-container">
        <textarea
          ref={textareaRef}
          className="text-area-input"
          placeholder={placeholder}
          value={synthesisRequest.text}
          onChange={handleChangeText}
          onSelect={getHighlightedText}
          onTouchEnd={getHighlightedText}
          onKeyDown={handleKeyDown}
        />
        <div className="page-container-footer">
          <div className="preview-group">
            {showSample ? <div /> : renderPreviewButton()}
            {!showSample && !hidePreviewDescription && (
              <div className="box-preview-desc">
                <p className="preview-desc">
                  {t('previewConditionNote')}
                  {'. '}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleHidePreviewDescription}
                    className="preview-understood"
                  >
                    {t('understand')}
                  </span>
                </p>
              </div>
            )}
          </div>
          <div className="count-characters">
            {inputLength?.toLocaleString(language) || 0}
            <span className="count-characters-max">
              /{maxLengthInputText.toLocaleString(language)}
            </span>
          </div>
        </div>

        <div className="tool-row">
          {showSample && (
            <SampleScripts
              audioRef={audioRef}
              sampleScripts={sampleScripts}
              onMobile={onMobile}
            />
          )}
        </div>
      </div>
      <AudioPlayer
        audioRef={audioRef}
        voice={voice}
        speed={synthesisRequest.speed}
        buttonStatus={buttonStatus}
        enableEditor={enableEditor}
        canUserUseVoiceCloning={canUserUseVoiceCloning}
        onEnableEditor={handleEnableEditor}
        handleChangeVoice={handleChangeVoice}
        onCreateSynthesis={handleCreateSynthesis}
        activeVoiceId={voice?.id}
        onChangeOpenWrongSyntaxDialog={handleChangeOpenWrongSyntaxDialog}
        onChangeButtonStatus={handleChangeButtonStatus}
        onSelectBreakTime={insertBreakTime}
        onRemovePreviewAudio={removePreviewAudio}
        onShowDialogUpgradeVoiceCloning={handleShowDialogUpgrade}
        showWarningCannotUseVoiceDialog={showWarningCannotUseVoiceDialog}
        BUTTON_STATUS={BUTTON_STATUS}
      />
      <NotificationDialogMobile
        name={openWrongSyntaxDialog?.name}
        title={openWrongSyntaxDialog?.title}
        description={openWrongSyntaxDialog?.description}
        subDescription={openWrongSyntaxDialog?.subDescription}
        variant={openWrongSyntaxDialog?.variant}
        open={!!openWrongSyntaxDialog?.title}
        onClose={() => setOpenWrongSyntaxDialog(null)}
        actionComponent={
          openWrongSyntaxDialog?.actionComponent ||
          renderCloseButton(() => setOpenWrongSyntaxDialog(null))
        }
      />
      {!!openWrongSyntaxDialogV2?.title && (
        <NotificationDialogV2
          name={openWrongSyntaxDialogV2?.name}
          title={openWrongSyntaxDialogV2?.title}
          description={openWrongSyntaxDialogV2?.description}
          subDescription={openWrongSyntaxDialogV2?.subDescription}
          variant={openWrongSyntaxDialogV2?.variant}
          open={!!openWrongSyntaxDialogV2?.title}
          onClose={() => setOpenWrongSyntaxDialogV2(null)}
          actionComponent={renderActionsPopupV2()}
        />
      )}
      {actionDialog?.title && (
        <ActionDialog
          image={actionDialog?.image}
          open={!!actionDialog?.title}
          title={actionDialog?.title}
          description={actionDialog?.description}
          onClose={actionDialog?.onClose}
          actionComponents={actionDialog?.actionComponents}
        />
      )}

      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={renderLockedCycleButton()}
          open={!!openDialogLockedCycle?.title}
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}

      {!!notificationDialogV3?.title && (
        <NotificationDialogV3
          name={notificationDialogV3?.name}
          title={notificationDialogV3?.title}
          description={notificationDialogV3?.description}
          subDescription={notificationDialogV3?.subDescription}
          variant={notificationDialogV3?.variant}
          open={!!notificationDialogV3?.title}
          onClose={() => setNotificationDialogV3(null)}
          actionComponent={renderNotificationDialogV3(() =>
            setNotificationDialogV3(null),
          )}
        />
      )}

      <NotificationDialog
        closeIcon
        removePaddingAction
        hiddenIcon
        name="ssml"
        title={t('note')}
        variant="warning"
        open={openDraft}
        onClose={handleCloseDraft}
        actionComponent={renderConfirmLoadDraft(
          handleCloseDraft,
          handleChangeLoadDraft,
        )}
        contentComponent={
          <Typography sx={STYLE_CONTENT_RELOAD}>
            {t('reloadPreviousWorkContent')}
          </Typography>
        }
      />

      <ExpiredPackageAlert
        open={openExpiredPackageDialog}
        onClose={handleCloseExpiredPackageDialog}
      />

      {renderMaxCharsLimitExceededDialog()}

      {renderOutOfCreditsDialog()}
    </StyledMobileTTS>
  );
};

export default MobileTTS;
